import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Container, Header } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import {
  AssetDownloadsTable,
  Wrappers,
  PageHeader,
  Loader
} from '../components';
import { withPageViewTracking, withAnalytics } from '../analytics';
import { priceListAPIs } from '../api';
import { withAuth, AuthRoles } from '../auth';
import { filterAuthorizedBrands } from '../utils/BrandHelper';
import { date } from '../utils/formatters';

const {
  swapImageEndpointForDownload,
  mapNameIfParentNull
} = AssetDownloadsTable.helpers;

const countryCodes = {
  Canada: 'CA',
  'United States': 'US'
};

const PriceLists = ({ authUser }) => {
  const [isLoading, setLoading] = useState(false);
  const [authorizedPriceLists, setAuthorizedPriceLists] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [userBrands, setUserBrands] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');

  const isAGIUser = AuthRoles.isAGIUser(authUser, 'roles');

  const getAuthorizedPriceLists = (priceListArray, brands) => {
    const filteredList = priceListArray
      .map(priceList => {
        const {
          ProductBrand,
          ProductFamily,
          FileLink,
          LastModified,
          AgiLocation
        } = priceList;
        // ensure expected attributes
        return {
          ...priceList,
          ProductBrand: mapNameIfParentNull(ProductBrand),
          ProductFamily: mapNameIfParentNull(ProductFamily),
          LastModified: date(LastModified),
          FileLink: swapImageEndpointForDownload(FileLink),
          AgiLocation: AgiLocation ? JSON.parse(AgiLocation) : null
        };
      })
      .filter(priceList => {
        const { AgiLocation } = priceList;
        if (!AgiLocation) return priceList;
        return (
          countryCodes[priceList.AgiLocation.DisplayName] === authUser.country
        );
      })
      .filter(priceList => {
        return brands.find(brand => {
          return brand.name === priceList.ProductBrand.Name;
        });
      });
    return filteredList;
  };

  useEffect(() => {
    const getPriceLists = async () => {
      const userBrands = filterAuthorizedBrands(authUser.brands, isAGIUser);
      try {
        setLoading(true);
        const priceListResult = await priceListAPIs.search({});
        const authorizedPriceLists = getAuthorizedPriceLists(
          priceListResult,
          userBrands
        );
        setAuthorizedPriceLists(authorizedPriceLists);
        setPriceLists(authorizedPriceLists);
      } catch (err) {
        return err;
      } finally {
        setLoading(false);
      }
      setUserBrands(userBrands);
    };
    getPriceLists();
  }, []);

  const handleBrandChange = brand => {
    setSelectedBrand(brand);
    if (!brand) {
      return setPriceLists(authorizedPriceLists);
    }
    const allPriceLists = authorizedPriceLists;
    const priceListsByBrand = allPriceLists.filter(
      list => list.ProductBrand.Name === brand
    );
    setPriceLists(priceListsByBrand);
  };

  const handleSearchInput = e => {
    const searchFilter = e.target.value.toLowerCase();
    setSearchFilter(searchFilter);
  };

  return (
    <Wrappers.Layout menu>
      <Container as="main">
        {isLoading ? (
          <Loader active />
        ) : (
          <Fragment>
            <PageHeader overlap>
              <Header as="h1" data-qa="pricelist-page-header">
                <FormattedMessage
                  id="title.priceLists"
                  description="Price List Title"
                  defaultMessage="Price Lists"
                />
              </Header>
            </PageHeader>

            <AssetDownloadsTable.FilterBar
              selectedBrand={selectedBrand}
              handleBrandChange={handleBrandChange}
              brands={userBrands}
              handleSearchInput={handleSearchInput}
            />
            <AssetDownloadsTable.Body
              data={priceLists || []}
              total={authorizedPriceLists && authorizedPriceLists.length}
              searchFilter={searchFilter}
              assetType="priceList"
              columns={[
                {
                  Header: 'Brand',
                  accessor: 'ProductBrand.Name',
                  sortType: 'basic'
                },
                {
                  Header: 'Product series',
                  accessor: 'ProductFamily.Name',
                  sortType: 'basic'
                },
                {
                  Header: 'Product name',
                  accessor: 'PriceListName',
                  sortType: 'basic',
                  Cell: AssetDownloadsTable.Cells.FileName
                },
                {
                  Header: 'Last Modified',
                  accessor: 'LastModified',
                  sortType: 'basic',
                  Cell: AssetDownloadsTable.Cells.FileName
                },
                {
                  Header: '',
                  accessor: 'FileLink',
                  Cell: AssetDownloadsTable.Cells.Download,
                  disableSortBy: true,
                  isCTA: true
                }
              ]}
            />
          </Fragment>
        )}
      </Container>
    </Wrappers.Layout>
  );
};

PriceLists.propTypes = {
  authUser: PropTypes.shape({}).isRequired
};

export default withPageViewTracking({ eventName: 'Price Lists Page Visit' })(
  withAnalytics(withAuth(PriceLists))
);
